/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { objectEntries, prettifySnakeCase } from '@poweralpha/common';
import {
  createFormHook,
  type DeepKeys,
  type StandardSchemaV1,
  type StandardSchemaV1Issue,
  type FormApi as TanstackFormApi,
  useStore,
} from '@tanstack/react-form';
import { useMemo } from 'react';
import type z from 'zod';
import ArrayTextFieldsPopper from './ArrayFormTextFieldPopper';
import FormArrayTextField from './FormArrayTextField';
import FormAutocomplete from './FormAutocomplete';
import FormComponentAutocomplete from './FormComponentAutocomplete';
import FormConfigAutocompleteWithEdit from './FormConfigAutocompleteWithEdit';
import { FormDateField } from './FormDateField';
import { FormDateRangeField } from './FormDateRangeField';
import FormFileButton from './FormFileButton';
import FormMonthlyDataFields from './FormMonthlyDataFields';
import FormProjectAutocomplete from './FormProjectAutocomplete';
import { FormSelect } from './FormSelect';
import FormSwitch from './FormSwitch';
import FormTextArea from './FormTextArea';
import FormTextField from './FormTextField';
import { FormToggleButtonGroup } from './FormToggleButtonGroup';
import { fieldContext, formContext } from './formContext';
import SalesforceField from './SalesforceField';
import SubmitButton from './SubmitButton';
import SubmitChip from './SubmitChip';

export const formHook = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    FormTextField,
    FormAutocomplete,
    ArrayTextFieldsPopper,
    FormFileButton,
    FormSwitch,
    FormMonthlyDataFields,
    FormComponentAutocomplete,
    FormSelect,
    FormDateField,
    FormDateRangeField,
    FormProjectAutocomplete,
    FormTextArea,
    FormArrayTextField,
    FormConfigAutocompleteWithEdit,
    FormToggleButtonGroup,
    SalesforceField,
  },
  formComponents: { SubmitButton, SubmitChip },
});

/**
 * TanStack React Form expects validators to conform to its `StandardSchemaV1` shape.
 * Zod v4 schemas implement this interface, so we type the parameter as `StandardSchemaV1`.
 *
 * Note: For transformed schemas, the form edits/validates the schema *input* type.
 * The schema output type is not used for the form's internal value typing.
 */
export const getStandardValidators = <TValues, TOutput = unknown>(
  schema: StandardSchemaV1<TValues, TOutput>,
) => ({
  onChange: schema,
  onSubmit: schema,
  onMount: schema,
});

export const getStandardValidatorsWithoutMount = <TValues, TOutput = unknown>(
  schema: StandardSchemaV1<TValues, TOutput>,
) => ({
  onChange: schema,
  onSubmit: schema,
});

/**
 * Convenience type exports used by various `toForm.ts` helpers.
 * Keep these broad enough to work across forms with different validator setups.
 */
export type FormApi<T, SubmitMeta = never> = TanstackFormApi<
  T,
  // biome-ignore lint/suspicious/noExplicitAny: generic
  any,
  // biome-ignore lint/suspicious/noExplicitAny: generic
  any,
  // biome-ignore lint/suspicious/noExplicitAny: generic
  any,
  // biome-ignore lint/suspicious/noExplicitAny: generic
  any,
  // biome-ignore lint/suspicious/noExplicitAny: generic
  any,
  // biome-ignore lint/suspicious/noExplicitAny: generic
  any,
  // biome-ignore lint/suspicious/noExplicitAny: generic
  any,
  // biome-ignore lint/suspicious/noExplicitAny: generic
  any,
  // biome-ignore lint/suspicious/noExplicitAny: generic
  any,
  // biome-ignore lint/suspicious/noExplicitAny: generic
  any,
  SubmitMeta
>;

export type FormSetFieldValue<T> = FormApi<T>['setFieldValue'];
export type FormRemoveFieldValue<T> = FormApi<T>['removeFieldValue'];

export type FormStore<T> = FormApi<T>['store'];

export type FormErrors<T> = Partial<Record<DeepKeys<T>, Array<StandardSchemaV1Issue>>>;

export type FieldHandleChange<T> = (values: T | ((values: T) => T)) => void;

export { clearFormValidationMeta } from './formValidationMeta';

export const useFormErrors = <T>(
  store: FormStore<T>,
  filter?: Array<DeepKeys<T>> | RegExp,
): FormErrors<T> => {
  const onMountErrors = useStore(store, (state) => state?.errorMap?.onMount);
  const onChangeErrors = useStore(store, (state) => state?.errorMap?.onChange);
  const onSubmitErrors = useStore(store, (state) => state?.errorMap?.onSubmit);
  const filterKey = useMemo(() => {
    if (!filter) return '';
    if (Array.isArray(filter)) {
      return `array:${JSON.stringify(filter.map(String))}`;
    }
    return `regex:${JSON.stringify({ source: filter.source, flags: filter.flags })}`;
  }, [filter]);

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: fine
  return useMemo(() => {
    const errors = {
      ...(onMountErrors ?? {}),
      ...(onChangeErrors ?? {}),
      ...(onSubmitErrors ?? {}),
    } as FormErrors<T>;

    if (!filterKey) return errors;

    const filterPrefixes = filterKey.startsWith('array:')
      ? (JSON.parse(filterKey.slice('array:'.length)) as Array<string>)
      : undefined;
    const filterRegex = filterKey.startsWith('regex:')
      ? (() => {
          const { source, flags } = JSON.parse(filterKey.slice('regex:'.length)) as {
            source: string;
            flags: string;
          };
          return new RegExp(source, flags);
        })()
      : undefined;
    const entries = objectEntries(errors);
    const filteredErrors: FormErrors<T> = {};
    if (filterPrefixes) {
      for (const [errorKey, value] of entries) {
        const errorKeyString = errorKey as string;
        if (filterPrefixes.some((key) => errorKeyString.startsWith(key))) {
          filteredErrors[errorKey as DeepKeys<T>] = value as Array<StandardSchemaV1Issue>;
        }
      }
      return filteredErrors;
    }

    for (const [key, value] of entries) {
      if (filterRegex?.test(key as string)) {
        filteredErrors[key as DeepKeys<T>] = value as Array<StandardSchemaV1Issue>;
      }
    }
    return filteredErrors;
  }, [filterKey, onMountErrors, onChangeErrors, onSubmitErrors]);
};

export const getErrorMessage = (name: string, issue: z.core.$ZodIssue) => {
  const formattedName = prettifySnakeCase(name);
  switch (issue.code) {
    case 'too_small':
      if (issue.origin === 'string') {
        return `${formattedName} must be at least ${issue.minimum} characters`;
      }
      if (issue.origin === 'array') {
        return `${formattedName} must have at least ${issue.minimum} items`;
      }
      if (['number', 'bigint', 'int'].includes(issue.origin)) {
        return `${formattedName} must be greater than or equal to ${issue.minimum}`;
      }
      return `${formattedName} is too small`;
    case 'too_big':
      if (issue.origin === 'string') {
        return `${formattedName} must be at most ${issue.maximum} characters`;
      }
      if (issue.origin === 'array') {
        return `${formattedName} must have at most ${issue.maximum} items`;
      }
      if (['number', 'bigint', 'int'].includes(issue.origin)) {
        return `${formattedName} must be less than or equal to ${issue.maximum}`;
      }
      return `${formattedName} is too big`;
    case 'invalid_type':
      return `${formattedName} must be of type ${issue.expected}`;
    case 'unrecognized_keys':
      return `${formattedName} has unrecognized keys: ${(issue.keys ?? []).join(', ')}`;
    default:
      // fallback for unknown codes
      return issue.message ?? `${formattedName} is invalid`;
  }
};
